import React, { Fragment, useContext, useLayoutEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, GridLegacy as Grid } from "@mui/material";
import { api, AssetLocation, AssetLocationTypes, IAssetLocationCreate, KeyManagementTypes } from "../../../logic/api";
import { SessionContext } from "../../../utility/useSession";
import { ITreeViewCommand, TreeViewCommand } from "../../../components/treeView/TreeView";
import EditableGroup, { EditableGroupType } from "../../../components/editable/EditableGroup/EditableGroup";
import { ProgressDialog } from "../../../components/notifications/progressDialog/progressDialog";
import ErrorDialog from "../../../components/notifications/errorDialog/ErrorDialog";
import LocationCreateSequentialDialog from "./LocationCreateSequentialDialog";
import { ReleaseFlag, useReleaseManagement } from "../../../utility/feature-management";
import { LocationFields, getAcceptableLocationTypes } from "../components/LocationFields";
import { useTranslation } from "react-i18next";
import { validateKeyLocation } from "../utils/keyStorageValidation";
import { useUserPermissions } from "../../../hooks/useUserPermissions";
import { isAdmin, isSysAdmin } from "../../../utility/utils";
import AvailabilityDateField from "../components/AvailabilityDateField";
import { isFutureLocalDate, toLocalMidnightUtc } from "../utils/availabilityDate";
import { usePreferences } from "../../../utility/usePreferences";
import SwitchField from "../../../components/editable/EditableField/fields/SwitchField";


const LocationCreateDialog: React.FunctionComponent<{
  parentLocation: AssetLocation | null;
  show: boolean;
  onClose: (command: ITreeViewCommand | null) => void;
}> = (props) => {
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const { timeZone } = usePreferences();
  const { hasPermission } = useUserPermissions();
  const isRbacReleased = useReleaseManagement(ReleaseFlag.releaseRbac);
  const canUpdateStatus = isRbacReleased
    ? hasPermission("assetTracking.location.updateStatus")
    : isSysAdmin(session) || isAdmin(session);

  const [location, setLocation] = useState<AssetLocation | null>();
  const keyLocationCreationEnabled = useReleaseManagement(ReleaseFlag.reinstateKeyLocationCreation);
  const isOpeningClosingDateReleased = useReleaseManagement(ReleaseFlag.releaseOpeningAndClosingDateZoneSettings);
  const showAvailability = isOpeningClosingDateReleased && location?.type === AssetLocationTypes.Zone;
  const createLocation = api.hooks.assetLocation.create();
  const acceptableLocationTypes = getAcceptableLocationTypes(keyLocationCreationEnabled, props.parentLocation);

  const [createParams, setCreateParams] = useState<IAssetLocationCreate | null>(null);
  const [showSeqDialog, setShowSeqDialog] = useState(false);
  const [err, setErr] = useState<Error | null>(null);
  const [isGpsLocationValid, setIsGpsLocationValid] = useState(true);
  const [closingDate, setClosingDate] = useState<Date | null>(null);
  const [isClosingDateInputValid, setIsClosingDateInputValid] = useState(true);

  const isClosingDateValid = isClosingDateInputValid
    && isFutureLocalDate(closingDate, timeZone);


  const getCreateParams = () => {
    if (location == null || (showAvailability && canUpdateStatus && !isClosingDateValid)) {
      return null;
    }
    const params: IAssetLocationCreate = {
      name: location.name,
      customerFacingName: location.customerFacingName,
      iata: location.iata,
      parentId: location.parentId,
      type: location.type,
      keyType: location.keyType,
      blockPark: location.blockPark == true,
      selfPark: location.selfPark == true,
      returns: location.returns == true,
      arrivals: location.arrivals == true,
      carWash: location.carWash === true,
      vip: location.vip === true,
      color: location.color,
      carParkId: location.carParkId,
      code: location.code,
      capacity: location.capacity,
      isRestrictedToVehicleLocation: location.isRestrictedToVehicleLocation == true,
      isAutoMoveLocation: location.isAutoMoveLocation == true,
      noMixedLaneAllocations: location.noMixedLaneAllocations,
      alertIfOverfilled: location.alertIfOverfilled,
      keyscanRestriction: location.keyscanRestriction,
      keyDEALRuleAction: location.keyDEALRuleAction,
      keyStorageRule: location.keyStorageRule,
      keyStorageDate: location.keyStorageDate,
      keyStorageTimeslotId: location.keyStorageTimeslotId || null,
      vehicleMovementRuleAction: location.vehicleMovementRuleAction,
      allowedParentIds: location.allowedParentIds,
      isAllocatable: Boolean(location.isAllocatable),
      isCollectionPoint: location.isCollectionPoint === true,
      isDeliveryPoint: location.isDeliveryPoint === true,
      gpsLocation: location.gpsLocation,
    };
    if (showAvailability && canUpdateStatus && closingDate) {
      params.closingDate = toLocalMidnightUtc(closingDate, timeZone).toISOString();
    }
    return params;
  }
  const sequentialCreate = () => {
    const params = getCreateParams();
    if (params == null) {
      return;
    }
    const errorMsg = validateKeyLocation(params, t);
    if (errorMsg) {
      setErr(new Error(errorMsg));
      return;
    }
    setCreateParams(params);
    setShowSeqDialog(true);
  }
  const save = () => {
    const params = getCreateParams();
    if (params == null) {
      return;
    }
    const errorMsg = validateKeyLocation(params, t);
    if (errorMsg) {
      setErr(new Error(errorMsg));
      return;
    }
    createLocation.send(session.current.context, params, () => {
      props.onClose({
        command: TreeViewCommand.reload,
        payLoad: props.parentLocation,
        targetId: null
      });
    });

  }


  useLayoutEffect(() => {
    if (props.show == true) {
      setIsGpsLocationValid(true);
      setClosingDate(null);
      setIsClosingDateInputValid(true);
      if (acceptableLocationTypes.length > 0) {
        setLocation({
          name: "",
          type: acceptableLocationTypes[0],
          parentId: props.parentLocation == null ? null : props.parentLocation.id,
          capacity: 0,
          blockPark: false,
          returns: false,
          keyType: KeyManagementTypes.inherit,
        } as AssetLocation);
      } else {
        setLocation(null);
      }
    }
  }, [props.show, props.parentLocation?.id]);



  return <Dialog open={props.show}>
    <DialogTitle>
      <Fragment>
        {t("locations.createNewLocation", { location: props.parentLocation == null ? "Top Level" : props.parentLocation.name })}
      </Fragment>
    </DialogTitle>
    <DialogContent>
      <ErrorDialog onClose={() => setErr(null)} err={err}></ErrorDialog>
      <ProgressDialog status={createLocation.status}></ProgressDialog>
      <ErrorDialog err={createLocation.err}></ErrorDialog>
      {createParams != null && (
        <LocationCreateSequentialDialog create={createParams} show={showSeqDialog} onClose={(result) => {
          setShowSeqDialog(false);
          if (result == true) {
            props.onClose({
              command: TreeViewCommand.reloadAll,
              payLoad: null,
              targetId: null
            });
          }
        }}></LocationCreateSequentialDialog>
      )}

      {location != null && (
        <Fragment>
          <div style={{ marginTop: 20 }}>

            <EditableGroup<AssetLocation>
              id="locSettings"
              testId="location-create-group"
              editType={EditableGroupType.alwaysEditing}
              data={location}
              onDataChanged={async (loc) => {
                setLocation(loc);
                return true;
              }}
            >
              <Grid container>
                <LocationFields
                  location={location}
                  parentLocation={props.parentLocation}
                  session={session}
                  onGpsLocationValidityChange={setIsGpsLocationValid}
                />
              </Grid>
            </EditableGroup>

          </div>
          {showAvailability && (
            <Fragment>
              <div style={{ width: "100%", marginTop: 20, marginBottom: 20 }}>
                <b>{t("locations.availability")}</b>
              </div>
              <Grid container alignItems="flex-end">
                <Grid item xs={12} sm={6}>
                  <SwitchField
                    ariaLabel={t("locations.active")}
                    label={t("locations.active")}
                    value
                    readOnly
                    onChange={() => undefined}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <AvailabilityDateField
                    disabled={!canUpdateStatus}
                    inputIsValid={isClosingDateInputValid}
                    label={t("locations.closingDate")}
                    testId="location-create-closingDate-date-picker"
                    value={closingDate}
                    onChange={setClosingDate}
                    onInputValidityChange={setIsClosingDateInputValid}
                  />
                </Grid>
              </Grid>
            </Fragment>
          )}
        </Fragment>
      )}

    </DialogContent>
    <DialogActions>
      <Button data-testid="cancel-create-location-button" onClick={() => {
        props.onClose(null);
      }}>
        {t("general.cancel")}
      </Button>
      <Button data-testid="rapid-create-location-button" disabled={location == null || location.name.length == 0 || !isGpsLocationValid || (showAvailability && !isClosingDateValid)} onClick={() => {
        sequentialCreate();
      }}>
        <b>{t("locations.rapidCreate")}</b>
      </Button>
      <Button data-testid="save-create-location-button" disabled={location == null || location.name.length == 0 || !isGpsLocationValid || (showAvailability && !isClosingDateValid)} onClick={() => {
        save();
      }}>
        <b>{t("general.save")}</b>
      </Button>
    </DialogActions>
  </Dialog>
}

export default LocationCreateDialog
